import React, { useState } from "react";
import {
    Input, InputNumber, Slider, Select, Radio,
    Switch, Button, message, Space, Divider, Typography, theme, Upload, List,
} from "antd";
import {
    SendOutlined, LoadingOutlined,
    FileImageOutlined, InboxOutlined,
    CloseCircleFilled, FileOutlined, DeleteOutlined,
} from "@ant-design/icons";
import styles from "./styles/AIToolBox.module.css";
import { useUploadMedia } from "../_actions/bot";

const { Text } = Typography;

function initValues(inputs) {
    const vals = {};
    inputs.forEach((field) => {
        vals[field.name] = field.default_value !== undefined && field.default_value !== null
            ? field.default_value
            : field.type === "toggle" ? false
            : field.type === "tags" ? []
            : "";
    });
    return vals;
}

function formatSize(bytes) {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// Single image or file upload
function SingleUploadField({ field, value, onChange, onUploadingChange }) {
    const isImage = field.type === "image";
    const { token } = theme.useToken();
    const [preview, setPreview] = useState(null); // { url, name, size }
    const [uploading, setUploading] = useState(false);

    const mutationUpload = useUploadMedia(() => {}, () => {});

    const accept = field.accept || (isImage ? "image/*" : undefined);

    const handleUpload = (file) => {
        setUploading(true);
        onUploadingChange(field.name, true);
        const localUrl = isImage ? URL.createObjectURL(file) : null;

        const formData = new FormData();
        formData.append("file", file?.originFileObj || file);
        mutationUpload.mutate(formData, {
            onSuccess: (result) => {
                setUploading(false);
                onUploadingChange(field.name, false);
                if (result?.success) {
                    setPreview({ url: localUrl, name: file.name, size: file.size });
                    onChange(result?.data?.id);
                }
            },
            onError: () => {
                setUploading(false);
                onUploadingChange(field.name, false);
                message.error(`Failed to upload ${field.label}`);
            },
        });
    };

    const handleRemove = () => {
        setPreview(null);
        onChange(null);
    };

    // Filled state — show preview row
    if (value) {
        return (
            <div
                style={{
                    border: `1px solid ${token.colorBorderSecondary}`,
                    borderRadius: token.borderRadiusLG,
                    overflow: "hidden",
                    background: token.colorFillQuaternary,
                }}
            >
                <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 14px" }}>
                    {/* Thumbnail or file icon */}
                    <div
                        style={{
                            width: 56,
                            height: 56,
                            borderRadius: token.borderRadius,
                            overflow: "hidden",
                            flexShrink: 0,
                            border: `1px solid ${token.colorBorderSecondary}`,
                            background: "#fff",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                        }}
                    >
                        {isImage && preview?.url ? (
                            <img
                                src={preview.url}
                                alt="preview"
                                style={{ width: "100%", height: "100%", objectFit: "cover" }}
                            />
                        ) : (
                            isImage
                                ? <FileImageOutlined style={{ fontSize: 22, color: token.colorPrimary }} />
                                : <FileOutlined style={{ fontSize: 22, color: token.colorPrimary }} />
                        )}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                        <Text
                            ellipsis
                            style={{ display: "block", fontSize: 13, fontWeight: 500, color: token.colorText }}
                        >
                            {preview?.name || "Uploaded"}
                        </Text>
                        {preview?.size != null && (
                            <Text style={{ fontSize: 12, color: token.colorTextTertiary }}>
                                {formatSize(preview.size)}
                            </Text>
                        )}
                    </div>
                    <CloseCircleFilled
                        onClick={handleRemove}
                        style={{ fontSize: 18, color: token.colorTextQuaternary, cursor: "pointer", flexShrink: 0, transition: "color 0.2s" }}
                        onMouseEnter={(e) => (e.currentTarget.style.color = token.colorError)}
                        onMouseLeave={(e) => (e.currentTarget.style.color = token.colorTextQuaternary)}
                    />
                </div>
            </div>
        );
    }

    // Empty state — dragger
    return (
        <Upload.Dragger
            accept={accept}
            showUploadList={false}
            beforeUpload={(file) => { handleUpload(file); return false; }}
            disabled={uploading}
            style={{ borderRadius: token.borderRadiusLG }}
        >
            <div style={{ padding: "8px 0" }}>
                <p className="ant-upload-drag-icon" style={{ marginBottom: 8 }}>
                    {uploading
                        ? <LoadingOutlined style={{ fontSize: 32, color: token.colorPrimary }} />
                        : isImage
                        ? <FileImageOutlined style={{ fontSize: 32, color: token.colorPrimary }} />
                        : <InboxOutlined style={{ fontSize: 32, color: token.colorPrimary }} />
                    }
                </p>
                <p style={{ margin: 0, fontSize: 13, fontWeight: 500, color: token.colorText }}>
                    {uploading ? "Uploading…" : `Click or drag ${isImage ? "an image" : "a file"} here`}
                </p>
                <p style={{ margin: "4px 0 0", fontSize: 12, color: token.colorTextTertiary }}>
                    {isImage ? "PNG, JPG, WEBP supported" : "Any file type supported"}
                </p>
            </div>
        </Upload.Dragger>
    );
}

// Multiple images or files upload
function MultiUploadField({ field, value = [], onChange, onUploadingChange }) {
    const isImage = field.type === "images";
    const { token } = theme.useToken();
    const [previews, setPreviews] = useState([]); // [{ mediaId, url, name, size }]
    const [uploading, setUploading] = useState(false);

    const mutationUpload = useUploadMedia(() => {}, () => {});

    const accept = field.accept || (isImage ? "image/*" : undefined);

    const handleUpload = (file) => {
        setUploading(true);
        onUploadingChange(field.name, true);
        const localUrl = isImage ? URL.createObjectURL(file) : null;

        const formData = new FormData();
        formData.append("file", file?.originFileObj || file);
        mutationUpload.mutate(formData, {
            onSuccess: (result) => {
                setUploading(false);
                onUploadingChange(field.name, false);
                if (result?.success) {
                    const mediaId = result?.data?.id;
                    setPreviews((prev) => [...prev, { mediaId, url: localUrl, name: file.name, size: file.size }]);
                    onChange([...(Array.isArray(value) ? value : []), mediaId]);
                }
            },
            onError: () => {
                setUploading(false);
                onUploadingChange(field.name, false);
                message.error(`Failed to upload ${file.name}`);
            },
        });
    };

    const handleRemove = (mediaId) => {
        setPreviews((prev) => prev.filter((p) => p.mediaId !== mediaId));
        onChange((Array.isArray(value) ? value : []).filter((id) => id !== mediaId));
    };

    return (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            <Upload.Dragger
                accept={accept}
                showUploadList={false}
                beforeUpload={(file) => { handleUpload(file); return false; }}
                multiple
                disabled={uploading}
                style={{ borderRadius: token.borderRadiusLG }}
            >
                <div style={{ padding: "8px 0" }}>
                    <p className="ant-upload-drag-icon" style={{ marginBottom: 8 }}>
                        {uploading
                            ? <LoadingOutlined style={{ fontSize: 32, color: token.colorPrimary }} />
                            : isImage
                            ? <FileImageOutlined style={{ fontSize: 32, color: token.colorPrimary }} />
                            : <InboxOutlined style={{ fontSize: 32, color: token.colorPrimary }} />
                        }
                    </p>
                    <p style={{ margin: 0, fontSize: 13, fontWeight: 500, color: token.colorText }}>
                        {uploading ? "Uploading…" : `Click or drag ${isImage ? "images" : "files"} here`}
                    </p>
                    <p style={{ margin: "4px 0 0", fontSize: 12, color: token.colorTextTertiary }}>
                        {isImage ? "PNG, JPG, WEBP supported · Select multiple" : "Any file type · Select multiple"}
                    </p>
                </div>
            </Upload.Dragger>

            {previews.length > 0 && (
                <List
                    size="small"
                    dataSource={previews}
                    style={{
                        border: `1px solid ${token.colorBorderSecondary}`,
                        borderRadius: token.borderRadiusLG,
                        overflow: "hidden",
                    }}
                    renderItem={(item) => (
                        <List.Item
                            style={{ padding: "8px 12px" }}
                            actions={[
                                <DeleteOutlined
                                    key="remove"
                                    onClick={() => handleRemove(item.mediaId)}
                                    style={{ color: token.colorTextQuaternary, cursor: "pointer" }}
                                    onMouseEnter={(e) => (e.currentTarget.style.color = token.colorError)}
                                    onMouseLeave={(e) => (e.currentTarget.style.color = token.colorTextQuaternary)}
                                />,
                            ]}
                        >
                            <List.Item.Meta
                                avatar={
                                    isImage && item.url ? (
                                        <div
                                            style={{
                                                width: 36,
                                                height: 36,
                                                borderRadius: token.borderRadius,
                                                overflow: "hidden",
                                                border: `1px solid ${token.colorBorderSecondary}`,
                                                flexShrink: 0,
                                            }}
                                        >
                                            <img
                                                src={item.url}
                                                alt={item.name}
                                                style={{ width: "100%", height: "100%", objectFit: "cover" }}
                                            />
                                        </div>
                                    ) : (
                                        <FileOutlined style={{ fontSize: 18, color: token.colorPrimary, marginTop: 6 }} />
                                    )
                                }
                                title={
                                    <Text ellipsis style={{ fontSize: 13, fontWeight: 500, maxWidth: 220, display: "block" }}>
                                        {item.name}
                                    </Text>
                                }
                                description={
                                    <Text style={{ fontSize: 12, color: token.colorTextTertiary }}>
                                        {formatSize(item.size)}
                                    </Text>
                                }
                            />
                        </List.Item>
                    )}
                />
            )}
        </div>
    );
}

function ToolInputForm({ inputs = [], onSubmit, loading }) {
    const sorted = [...inputs].sort((a, b) => a.order - b.order);
    const [values, setValues] = useState(() => initValues(sorted));
    const [errors, setErrors] = useState({});
    const [uploading, setUploading] = useState({});
    const { token } = theme.useToken();

    const setUploadingField = (name, isUploading) =>
        setUploading((prev) => ({ ...prev, [name]: isUploading }));

    const setValue = (name, val) => setValues((prev) => ({ ...prev, [name]: val }));

    const validate = () => {
        const errs = {};
        sorted.forEach((field) => {
            if (field.is_required) {
                const val = values[field.name];
                const empty =
                    val === null || val === undefined || val === "" ||
                    (Array.isArray(val) && val.length === 0);
                if (empty) errs[field.name] = `${field.label} is required`;
            }
        });
        setErrors(errs);
        return Object.keys(errs).length === 0;
    };

    const handleSubmit = () => {
        if (!validate()) {
            message.error("Please fill in all required fields");
            return;
        }
        onSubmit(values);
    };

    const isUploadingAny = Object.values(uploading).some(Boolean);

    const labelStyle = {
        display: "block",
        marginBottom: 8,
        fontWeight: 500,
        fontSize: 13,
        color: token.colorTextSecondary,
        textTransform: "uppercase",
        letterSpacing: "0.04em",
    };

    const renderField = (field) => {
        const val = values[field.name];
        const set = (v) => setValue(field.name, v);

        // File/image types — handled separately below
        if (["image", "file"].includes(field.type)) {
            return (
                <SingleUploadField
                    field={field}
                    value={val}
                    onChange={set}
                    onUploadingChange={setUploadingField}
                />
            );
        }
        if (["images", "files"].includes(field.type)) {
            return (
                <MultiUploadField
                    field={field}
                    value={val}
                    onChange={set}
                    onUploadingChange={setUploadingField}
                />
            );
        }

        switch (field.type) {
            case "text":
                return (
                    <Input
                        value={val}
                        onChange={(e) => set(e.target.value)}
                        placeholder={field.placeholder || ""}
                        size="large"
                    />
                );
            case "textarea":
                return (
                    <Input.TextArea
                        value={val}
                        onChange={(e) => set(e.target.value)}
                        placeholder={field.placeholder || ""}
                        rows={4}
                    />
                );
            case "number":
                return (
                    <InputNumber
                        value={val}
                        onChange={set}
                        min={field.min}
                        max={field.max}
                        step={field.step || 1}
                        size="large"
                        style={{ width: "100%" }}
                    />
                );
            case "slider":
                return (
                    <div className={styles.sliderRow}>
                        <Slider
                            value={val === "" ? (field.min || 0) : val}
                            onChange={set}
                            min={field.min}
                            max={field.max}
                            step={field.step || 1}
                        />
                        <InputNumber
                            value={val === "" ? (field.min || 0) : val}
                            onChange={set}
                            min={field.min}
                            max={field.max}
                            step={field.step || 1}
                            style={{ width: 80 }}
                        />
                    </div>
                );
            case "dropdown":
                return (
                    <Select
                        value={val || undefined}
                        onChange={set}
                        size="large"
                        style={{ width: "100%" }}
                        options={(field.options || []).map((o) => ({ label: o, value: o }))}
                    />
                );
            case "radio":
                return (
                    <Radio.Group
                        value={val}
                        onChange={(e) => set(e.target.value)}
                        optionType="button"
                        buttonStyle="solid"
                    >
                        {(field.options || []).map((o) => (
                            <Radio.Button key={o} value={o}>{o}</Radio.Button>
                        ))}
                    </Radio.Group>
                );
            case "color":
                return (
                    <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                        <input
                            type="color"
                            value={val || "#000000"}
                            onChange={(e) => set(e.target.value)}
                            style={{ width: 48, height: 40, padding: 2, border: `1px solid ${token.colorBorder}`, borderRadius: token.borderRadius, cursor: "pointer", background: "none" }}
                        />
                        <Input
                            value={val || ""}
                            onChange={(e) => set(e.target.value)}
                            placeholder="#000000"
                            size="large"
                            style={{ flex: 1 }}
                        />
                    </div>
                );
            case "toggle":
                return <Switch checked={!!val} onChange={set} />;
            case "tags":
                return (
                    <Select
                        mode="tags"
                        value={Array.isArray(val) ? val : []}
                        onChange={set}
                        size="large"
                        style={{ width: "100%" }}
                        placeholder="Type and press Enter"
                        open={false}
                    />
                );
            default:
                return null;
        }
    };

    return (
        <Space direction="vertical" size={16} style={{ width: "100%" }}>
            {sorted.map((field, index) => (
                <React.Fragment key={field.name}>
                    <div>
                        <Text style={labelStyle}>
                            {field.label}
                            {field.is_required && (
                                <span style={{ color: token.colorError }}> *</span>
                            )}
                        </Text>
                        {renderField(field)}
                        {errors[field.name] && (
                            <div className={styles.errorText}>{errors[field.name]}</div>
                        )}
                    </div>
                    {index < sorted.length - 1 && <Divider style={{ margin: 0 }} />}
                </React.Fragment>
            ))}

            <Button
                type="primary"
                size="large"
                icon={<SendOutlined />}
                onClick={handleSubmit}
                loading={loading}
                disabled={isUploadingAny}
                block
                style={{ marginTop: 4 }}
            >
                {loading ? "Generating…" : "Generate"}
            </Button>
        </Space>
    );
}

export default ToolInputForm;
export { initValues };
